import { useState, ReactNode, useEffect } from 'react';

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

/**
 * A styled collapsible container for grouping UI elements.
 * Features smooth transitions, refined typography, and modern toggle styling.
 */
export function CollapsibleSection({ title, children, defaultOpen = false, className = '' }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Sync with defaultOpen if it changes (e.g. when selecting a new element type)
  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <div className={`flex flex-col border-b border-surface-border dark:border-neutral-800/60 last:border-0 ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full group flex items-center justify-between py-3.5 text-left focus:outline-none"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-text-muted dark:text-neutral-500 group-hover:text-game-blue transition-colors">
          {title}
        </span>
        <div className={`flex items-center justify-center w-6 h-6 rounded-full transition-all duration-300 ${
          isOpen 
            ? 'rotate-180 text-game-blue bg-game-blue/10' 
            : 'text-text-muted/40 group-hover:text-text-muted group-hover:bg-surface-secondary dark:group-hover:bg-neutral-800'
        }`}>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[2000px] opacity-100 pb-5' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="animate-fade-in">
          {children}
        </div>
      </div>
    </div>
  );
}
